import React, { useEffect, useState } from "react";
import {
  useParams,
  useLocation as usePath,
  Route,
  Redirect,
  Switch
} from "react-router-dom";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import {
  administrationPosition,
  administrationPositions,
  administrationPositionInfo,
  administrationPositionAssignments,
  administrationPositionAddAssignment,
  buildPositionInformationRoute,
  buildPositionAssignmentsRoute,
  buildAddPositionAssignmentRoute
} from "../../routes/AdminRoutes";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import { usePosition } from "../../hooks/positions";
import PositionInformation from "../../views/Positions/PositionInformation";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { useTranslation } from "react-i18next";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../redux/actions/snackbarActions";
import AssignmentsTable from "../../views/Assignments/AssignmentsTable";
import Tabs from "../../components/Tabs/Tabs";
import { Box } from "@mui/material";
import AddAssignment from "../../views/Assignments/AddAssignment";
import PrivateRoute from "../../components/PrivateRoute/PrivateRoute";
import usePermissions from "../../redux/selectors/usePermissions";
import { useReport } from "../../hooks/useReport";

const AdminPositionDetails = ({
  positionId,
  position,
  onUpdatePosition,
  isLoading,
  onAddAssignmentClick
}) => {
  const currentPath = usePath();
  const { t } = useTranslation();
  const { isGoldOrPlatinum } = usePermissions();
  const dispatch = useDispatch();

  const [currentTab, setCurrentTab] = useState(0);

  const handleGoBack = () => {
    dispatch(push(administrationPositions));
  };

  const openPrintReport = useReport("Training Assignment by Position Report", {
    filters: {
      position_name: position?.name ? `${positionId}|${position.name}` : null
    },
    invoke: "print"
  });

  const openMailReport = useReport("Training Assignment by Position Report", {
    filters: {
      position_name: position?.name ? `${positionId}|${position.name}` : null
    },
    invoke: "email"
  });

  const tabs = [
    {
      label: t("administration.tabs.positionInfoTab"),
      path: buildPositionInformationRoute(positionId),
      disabled: false
    },
    {
      label: t("administration.tabs.assignmentsTab"),
      path: buildPositionAssignmentsRoute(positionId),
      disabled: !isGoldOrPlatinum,
      tooltip: !isGoldOrPlatinum
        ? t("administration.goldPlatinumFeatureTooltip")
        : ""
    }
  ];

  useEffect(() => {
    const currentPathIndex = tabs.findIndex(
      tab => tab.path === currentPath.pathname
    );
    if (currentPathIndex !== -1) {
      setCurrentTab(currentPathIndex);
    }
  }, [currentPath]);

  const handleTabChange = newTab => {
    setCurrentTab(newTab);
    dispatch(push(tabs[newTab].path));
  };

  const handleUpdatePosition = newPositionData => {
    return onUpdatePosition(newPositionData).then(response => {
      if (response.errors) {
        dispatch(
          enqueueErrorSnackbar({
            message: t("administration.updatePositionErrorMessage")
          })
        );
      } else {
        dispatch(
          enqueueSuccessSnackbar({
            message: t("administration.updatePositionSuccessMessage")
          })
        );
      }
    });
  };

  return (
    <AdminPageLayout
      title={
        <Breadcrumb routes={["Positions", position ? position.name : ""]} />
      }
      onGoBackClick={handleGoBack}
    >
      <Tabs
        tabs={tabs}
        currentTab={currentTab}
        onChange={newTab => handleTabChange(newTab)}
        scrollButtons={false}
        textVariant="h6"
      />
      <Box my={3}>
        <Route
          exact
          path={administrationPosition}
          render={() => {
            return <Redirect to={buildPositionInformationRoute(positionId)} />;
          }}
        />
        <Route
          exact
          path={administrationPositionInfo}
          render={() => {
            return (
              <PositionInformation
                position={position || {}}
                isLoading={isLoading}
                positionId={positionId}
                onUpdatePosition={handleUpdatePosition}
              />
            );
          }}
        />
        <PrivateRoute
          permitted={isGoldOrPlatinum}
          redirectRoute={administrationPositions}
          exact
          path={administrationPositionAssignments}
          render={() => {
            return (
              <AssignmentsTable
                referenceType="position"
                referenceId={positionId}
                onAddAssignmentClick={onAddAssignmentClick}
                onOpenMailReport={openMailReport}
                onOpenPrintReport={openPrintReport}
              />
            );
          }}
        />
      </Box>
    </AdminPageLayout>
  );
};

const AdminPosition = () => {
  const { positionId } = useParams();
  const { position, updatePosition, isLoading } = usePosition(positionId, [
    "groups",
    "roles"
  ]);
  const dispatch = useDispatch();

  const handleAddAssignmentClick = () => {
    dispatch(push(buildAddPositionAssignmentRoute(positionId)));
  };

  const handleGoToAssignmentsView = () => {
    dispatch(push(buildPositionAssignmentsRoute(positionId)));
  };

  return (
    <Switch>
      <Route
        exact
        path={administrationPositionAddAssignment}
        render={() => {
          return (
            <AddAssignment
              referenceType="position"
              referenceId={positionId}
              referenceName={position ? position.name : ""}
              onGoBackClick={handleGoToAssignmentsView}
            />
          );
        }}
      />
      <Route
        path={[administrationPositionInfo, administrationPositionAssignments]}
        render={() => (
          <AdminPositionDetails
            positionId={positionId}
            isLoading={isLoading}
            position={position}
            onUpdatePosition={updatePosition}
            onAddAssignmentClick={handleAddAssignmentClick}
          />
        )}
      />
    </Switch>
  );
};
export default AdminPosition;
